import { useEffect, useState, useCallback } from 'react';
import { Card, Switch, Button, Table, Tag, Select, Statistic, Row, Col, Spin, message, Typography, Space, Descriptions, Modal, Input, List, InputNumber, Tooltip, Progress, Alert, Popconfirm } from 'antd';
import { PlayCircleOutlined, HistoryOutlined, BarChartOutlined, ThunderboltOutlined, ClockCircleOutlined, DatabaseOutlined, FolderOpenOutlined, SearchOutlined, FileTextOutlined, EyeOutlined, EyeInvisibleOutlined, SyncOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';
import './AiRefinePanel.css';

const { Text } = Typography;

// 通过 Nginx /kb/ 代理访问 KB Server（避免 Mixed Content）
const KB_URL = '/kb/api';
const KB_TOKEN = 'kb_H7ZYRcNO1BPW5Bxu';
const kbFetch = async (path: string, options?: RequestInit) => {
  const res = await fetch(`${KB_URL}${path}`, {
    ...options,
    headers: { 'Authorization': `Bearer ${KB_TOKEN}`, 'Content-Type': 'application/json', ...(options?.headers || {}) },
  });
  const text = await res.text();
  let data: any = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = { success: false, error: text || `HTTP ${res.status}` }; }
  return { ...data, httpStatus: res.status, ok: res.ok };
};

interface TaskConfig {
  id: string; taskType: 'doc' | 'exp' | 'web'; enabled: boolean;
  frequencyMinutes: number; maxPerRun: number;
  modelId: string | null; lastRunAt: string | null;
  model?: { id: string; name: string; code: string; provider?: { name: string } };
  isRunning: boolean;
  config?: { keywords?: string[]; maxContentAgeMonths?: number; runDurationMinutes?: number; };
}

interface LlmModel { id: string; name: string; code: string; provider?: { name: string }; }

interface RefineLog {
  id: string; taskId: string; taskType: 'doc' | 'exp' | 'web';
  status: 'running' | 'success' | 'failed';
  itemsProcessed: number; itemsRefined: number; itemsSkipped: number;
  details?: any[]; errorMessage?: string;
  startedAt: string; finishedAt: string | null;
}

interface StatsData {
  period: string;
  byType: Record<string, { processed: number; refined: number; skipped: number; runs: number }>;
}

interface KbCategory { id: string; name: string; description: string; color: string; }
interface KbDoc { name: string; type: string; chunkCount: number; size: number; categoryId: string; createdAt: string; }
type RefineCategory = KbCategory & { docCount: number; latestCreatedAt?: string | null };
type ClearProgressState = {
  open: boolean;
  catName: string;
  total: number;
  done: number;
  ok: number;
  failed: number;
  current: string;
  status: 'running' | 'success' | 'warning' | 'error';
};

const getDocCreatedTime = (doc: KbDoc) => {
  const time = dayjs(doc.createdAt);
  return time.isValid() ? time.valueOf() : 0;
};

const sortDocsByCreatedDesc = (docs: KbDoc[]) => [...docs].sort((a, b) => getDocCreatedTime(b) - getDocCreatedTime(a));

const formatDocTime = (value?: string | null) => {
  if (!value) return '时间未知';
  const time = dayjs(value);
  return time.isValid() ? time.format('YYYY-MM-DD HH:mm:ss') : '时间未知';
};

const TYPE_ICONS: Record<string, string> = { '文本': '📄', 'Markdown': '📝', 'PDF': '📕', 'Word': '📘', 'Excel': '📊', '未知': '📎' };
const TASK_LABELS: Record<string, string> = { doc: '本地提炼', exp: '经验提炼', web: '网络情报' };
const TASK_COLORS: Record<string, string> = { doc: '#52c41a', exp: '#722ed1', web: '#1890ff' };
const SOURCE_TAG: Record<string, { color: string; text: string }> = {
  '情报_': { color: 'blue', text: '情报' },
  '提炼_': { color: 'green', text: '提炼' },
};

const FREQ_OPTIONS = [
  { label: '每10分钟', value: 10 }, { label: '每30分钟', value: 30 },
  { label: '每1小时', value: 60 }, { label: '每2小时', value: 120 },
  { label: '每6小时', value: 360 },
];

export default function AiRefinePanel() {
  const [tasks, setTasks] = useState<TaskConfig[]>([]);
  const [logs, setLogs] = useState<RefineLog[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [statsPeriod, setStatsPeriod] = useState('daily');
  const [logLoading, setLogLoading] = useState(false);
  const [taskFilter, setTaskFilter] = useState<string>('all');
  const [logDetail, setLogDetail] = useState<RefineLog | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [models, setModels] = useState<LlmModel[]>([]);
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveCollecting, setLiveCollecting] = useState(false);
  const [lastLiveRefresh, setLastLiveRefresh] = useState<string | null>(null);
  const [taskNotices, setTaskNotices] = useState<Record<string, { type: 'info' | 'success' | 'error'; text: string }>>({});

  const [refineCats, setRefineCats] = useState<RefineCategory[]>([]);
  const [catLoading, setCatLoading] = useState(false);
  const [selectedCat, setSelectedCat] = useState<string | null>(null);
  const [catDocs, setCatDocs] = useState<KbDoc[]>([]);
  const [catDocsAll, setCatDocsAll] = useState<KbDoc[]>([]);
  const [docLoading, setDocLoading] = useState(false);
  const [docSearch, setDocSearch] = useState('');
  const [docSearchLoading, setDocSearchLoading] = useState(false);
  const [docDetail, setDocDetail] = useState<KbDoc | null>(null);
  const [docDetailText, setDocDetailText] = useState('');
  const [docDetailLoading, setDocDetailLoading] = useState(false);
  const [docPage, setDocPage] = useState(1);
  const [clearingCatId, setClearingCatId] = useState<string | null>(null);
  const [clearProgress, setClearProgress] = useState<ClearProgressState | null>(null);
  const PAGE_SIZE = 15;
  const configurableTasks = tasks.filter(task => task.taskType !== 'web');
  const liveTaskTypes = ['doc', 'exp'];

  const loadTasks = useCallback(async () => {
    try { const r = await api.get('/ai-refine/tasks'); if (r.data?.success) setTasks(r.data.data); } catch { /* */ }
  }, []);

  const loadModels = useCallback(async () => {
    try { const r = await api.get('/llm/models'); if (r.data) setModels(Array.isArray(r.data) ? r.data : []); } catch { /* */ }
  }, []);

  const loadLogs = useCallback(async (page = 1) => {
    setLogLoading(true);
    try {
      const r = await api.get('/ai-refine/logs', { params: { page, pageSize: 15, taskType: taskFilter === 'all' ? undefined : taskFilter } });
      if (r.data?.success) { setLogs(r.data.data.items); setLogTotal(r.data.data.total); }
    } catch { setLogs([]); }
    setLogLoading(false);
  }, [taskFilter]);

  const loadStats = useCallback(async () => {
    try { const r = await api.get('/ai-refine/stats', { params: { period: statsPeriod } }); if (r.data?.success) setStats(r.data.data); } catch { /* */ }
  }, [statsPeriod]);

  const loadRefineCats = useCallback(async () => {
    setCatLoading(true);
    try {
      const [catsRes, docsRes] = await Promise.all([kbFetch('/categories'), kbFetch('/documents')]);
      const allCats: KbCategory[] = catsRes.success ? catsRes.data : [];
      const allDocs: KbDoc[] = docsRes.success ? docsRes.data : [];
      const aiPrefixes = ['提炼·', 'AI情报·', '每日技术简报', '设计灵感库', '其他'];
      const refine = allCats
        .filter((c: KbCategory) => aiPrefixes.some(p => c.name.startsWith(p)) || c.name === '其他')
        .map((c: KbCategory) => {
          const docsInCat = allDocs.filter((d: KbDoc) => d.categoryId === c.id);
          const sortedDocs = sortDocsByCreatedDesc(docsInCat);
          return { ...c, docCount: docsInCat.length, latestCreatedAt: sortedDocs[0]?.createdAt || null };
        })
        .sort((a: RefineCategory, b: RefineCategory) => {
          const latestDiff = dayjs(b.latestCreatedAt || 0).valueOf() - dayjs(a.latestCreatedAt || 0).valueOf();
          if (latestDiff !== 0) return latestDiff;
          return a.name.localeCompare(b.name, 'zh-CN');
        });
      setRefineCats(refine);
    } catch { setRefineCats([]); }
    setCatLoading(false);
  }, []);

  const loadCatDocs = useCallback(async (catId: string) => {
    setDocLoading(true); setDocSearch(''); setDocPage(1);
    try {
      const docsRes = await kbFetch('/documents');
      const allDocs: KbDoc[] = docsRes.success ? docsRes.data : [];
      const filtered = sortDocsByCreatedDesc(allDocs.filter((d: KbDoc) => d.categoryId === catId));
      setCatDocsAll(filtered); setCatDocs(filtered);
    } catch { setCatDocsAll([]); setCatDocs([]); }
    setDocLoading(false);
  }, []);

  useEffect(() => { loadTasks(); loadModels(); loadStats(); loadRefineCats(); }, [loadTasks, loadModels, loadStats, loadRefineCats]);
  useEffect(() => { loadLogs(logPage); }, [loadLogs, logPage, taskFilter]);

  useEffect(() => {
    const hasRunning = tasks.some(t => t.isRunning);
    if (!hasRunning) return;
    const timer = setInterval(() => { loadTasks(); loadLogs(logPage); loadRefineCats(); }, 10000);
    return () => clearInterval(timer);
  }, [tasks, logPage, loadTasks, loadLogs, loadRefineCats]);

  const handleDocSearch = (value: string) => {
    setDocSearch(value); setDocPage(1);
    const keyword = value.trim();
    if (!keyword) { setCatDocs(catDocsAll); return; }
    // AI提炼分类搜索框会在输入过程中高频触发；1个字符/字母时只做前端文档名过滤，禁止轰炸KB向量搜索。
    if (keyword.length < 2) {
      const lower = keyword.toLowerCase();
      setCatDocs(sortDocsByCreatedDesc(catDocsAll.filter(d => d.name.toLowerCase().includes(lower))));
      return;
    }
    // 调用KB后端向量+关键词混合搜索。控制limit，避免AI提炼搜索一次拉过多LanceDB结果。
    setDocSearchLoading(true);
    kbFetch('/search', {
      method: 'POST',
      body: JSON.stringify({ query: keyword, limit: 20, categoryIds: selectedCat ? [selectedCat] : undefined }),
    }).then(res => {
      const hits: any[] = (res.success && res.data) ? res.data : [];
      // 搜索结果返回的是chunk，按文档名去重
      const hitNames = new Set<string>();
      for (const h of hits) { if (h.name) hitNames.add(h.name); }
      // 与当前分类下的全量文档做交集（categorId按前端已选过滤）
      const matched = sortDocsByCreatedDesc(
        catDocsAll.filter(d => hitNames.has(d.name))
      );
      setCatDocs(matched);
    }).catch(() => {
      setCatDocs([]);
    }).finally(() => {
      setDocSearchLoading(false);
    });
  };

  const clearCategoryDocs = async (cat: RefineCategory) => {
    if (!cat?.id) return;
    const aiPrefixes = ['提炼·', 'AI情报·', '每日技术简报', '设计灵感库', '其他'];
    const isAiRefineCategory = aiPrefixes.some(p => cat.name.startsWith(p)) || cat.name === '其他';
    if (!isAiRefineCategory) {
      message.error('安全拦截：只能清除 AI提炼 分类的数据，不能清除原有知识库分类');
      return;
    }

    setClearingCatId(cat.id);
    setClearProgress({ open: true, catName: cat.name, total: 0, done: 0, ok: 0, failed: 0, current: '正在读取分类文档...', status: 'running' });
    try {
      const docsRes = await kbFetch('/documents');
      const allDocs: KbDoc[] = docsRes.success ? docsRes.data : [];
      const docsToDelete = allDocs.filter((d: KbDoc) => d.categoryId === cat.id);
      if (docsToDelete.length === 0) {
        setClearProgress({ open: true, catName: cat.name, total: 0, done: 0, ok: 0, failed: 0, current: '该分类暂无可清除数据', status: 'success' });
        message.info('该分类暂无可清除数据');
        await loadRefineCats();
        if (selectedCat === cat.id) { setCatDocsAll([]); setCatDocs([]); setDocPage(1); }
        return;
      }

      let ok = 0;
      let failed = 0;
      setClearProgress({ open: true, catName: cat.name, total: docsToDelete.length, done: 0, ok: 0, failed: 0, current: '准备开始删除...', status: 'running' });
      for (let i = 0; i < docsToDelete.length; i += 1) {
        const doc = docsToDelete[i];
        const shortName = doc.name.length > 42 ? `${doc.name.slice(0, 42)}...` : doc.name;
        setClearProgress({ open: true, catName: cat.name, total: docsToDelete.length, done: i, ok, failed, current: shortName, status: 'running' });
        const r = await kbFetch('/documents/delete', {
          method: 'POST',
          body: JSON.stringify({ name: doc.name }),
        });
        if (r?.success) ok += 1; else failed += 1;
        setClearProgress({ open: true, catName: cat.name, total: docsToDelete.length, done: i + 1, ok, failed, current: shortName, status: 'running' });
      }

      await loadRefineCats();
      if (selectedCat === cat.id) {
        setCatDocsAll([]);
        setCatDocs([]);
        setDocPage(1);
        setDocSearch('');
      }

      const status = failed > 0 ? 'warning' : 'success';
      setClearProgress({ open: true, catName: cat.name, total: docsToDelete.length, done: docsToDelete.length, ok, failed, current: failed > 0 ? '部分删除失败，请刷新后重试' : '清除完成', status });
      if (failed > 0) message.warning(`已清除 ${ok} 条，${failed} 条失败，请刷新后重试`);
      else message.success(`已清除「${cat.name}」分类下 ${ok} 条数据`);
    } catch (e: any) {
      setClearProgress(prev => ({ open: true, catName: prev?.catName || cat.name, total: prev?.total || 0, done: prev?.done || 0, ok: prev?.ok || 0, failed: prev?.failed || 0, current: e?.message || '未知错误', status: 'error' }));
      message.error('清除失败：' + (e?.message || '未知错误'));
    } finally {
      setClearingCatId(null);
    }
  };

  const toggleTask = async (task: TaskConfig) => {
    try {
      const r = await api.put(`/ai-refine/tasks/${task.id}`, { enabled: !task.enabled });
      if (r.data?.success) {
        message.success(`${TASK_LABELS[task.taskType]}已${!task.enabled ? '开启' : '关闭'}`);
        loadTasks();
        refreshLiveStatus().catch(() => undefined);
      }
    } catch { message.error('操作失败'); }
  };

  const updateFreq = async (task: TaskConfig, freq: number) => {
    try { await api.put(`/ai-refine/tasks/${task.id}`, { frequencyMinutes: freq }); message.success('频率已更新'); loadTasks(); } catch { message.error('更新失败'); }
  };

  const updateModel = async (task: TaskConfig, modelId: string) => {
    try { await api.put(`/ai-refine/tasks/${task.id}`, { modelId: modelId || null }); message.success('模型已更新'); loadTasks(); } catch { message.error('更新失败'); }
  };

  const updateMaxPerRun = async (task: TaskConfig, v: number | null) => {
    if (!v) return;
    try { await api.put(`/ai-refine/tasks/${task.id}`, { maxPerRun: v }); loadTasks(); } catch { message.error('更新失败'); }
  };


  const [progress, setProgress] = useState<{
    taskId: string; logId: string; step: string; pct: number; steps: { step: string; progress: number; time: string }[];
  } | null>(null);

  // 提炼任务实时日志采集：用户点“开始查看”后才轮询，点关闭立即停止
  const [liveStatus, setLiveStatus] = useState<Record<string, any>>({});
  const refreshLiveStatus = useCallback(async () => {
    const r = await api.get('/ai-refine/live-status');
    if (r.data?.success) {
      setLiveStatus(r.data.data || {});
      setLastLiveRefresh(dayjs().format('HH:mm:ss'));
    }
  }, []);

  useEffect(() => {
    if (!liveCollecting) return;
    refreshLiveStatus().catch(() => undefined);
    const poll = setInterval(() => {
      refreshLiveStatus().catch(() => undefined);
      loadTasks();
      loadLogs(logPage);
    }, 3000);
    return () => clearInterval(poll);
  }, [liveCollecting, refreshLiveStatus, loadTasks, loadLogs, logPage]);

  const startLiveMonitor = () => {
    setLiveOpen(true);
    setLiveCollecting(true);
    message.info('已开始采集提炼任务实时日志');
  };

  const stopLiveMonitor = () => {
    setLiveCollecting(false);
    setLiveOpen(false);
    message.info('已关闭实时日志采集');
  };

  const triggerOnce = async (task: TaskConfig) => {
    console.log('[trigger] 点击按钮, taskId=', task.id, 'taskType=', task.taskType);
    setTriggering(task.id);
    setTaskNotices(prev => ({ ...prev, [task.id]: { type: 'info', text: '已点击，正在向后端发送启动请求…' } }));
    setLiveOpen(true);
    setLiveCollecting(true);
    try {
      console.log('[trigger] 发送POST /api/ai-refine/tasks/' + task.id + '/trigger');
      const r = await api.post(`/ai-refine/tasks/${task.id}/trigger`);
      console.log('[trigger] 收到响应:', JSON.stringify(r.data));
      if (r.data?.success) {
        const { logId } = r.data.data || {};
        console.log('[trigger] 成功, logId=', logId);
        if (!logId) {
          message.success('已触发');
          setTaskNotices(prev => ({ ...prev, [task.id]: { type: 'success', text: '后端已接收任务，正在刷新实时日志…' } }));
          setTriggering(null);
          refreshLiveStatus().catch(() => undefined);
          return;
        }
        
        message.info('任务已启动，正在执行...');
        setTaskNotices(prev => ({ ...prev, [task.id]: { type: 'info', text: `任务已启动，日志ID：${logId}，正在实时采集处理过程…` } }));
        setProgress({ taskId: task.id, logId, step: '初始化', pct: 0, steps: [] });
        setTriggering(null);
        refreshLiveStatus().catch(() => undefined);
        
        // 轮询进度，每2秒一次
        const poll = setInterval(async () => {
          try {
            console.log('[poll] 查询进度...');
            const pr = await api.get(`/ai-refine/logs/${logId}/progress`);
            console.log('[poll] 进度响应:', JSON.stringify(pr.data));
            if (pr.data?.success) {
              const d = pr.data.data;
              setProgress(prev => prev?.logId === logId ? {
                taskId: task.id, logId, step: d.step, pct: d.progress, steps: d.steps || [],
              } : prev);
              
              if (d.status === 'success' || d.status === 'failed') {
                clearInterval(poll);
                if (d.status === 'success') {
                  message.success(`完成！处理${d.processed}条，提炼${d.refined}条`);
                  setTaskNotices(prev => ({ ...prev, [task.id]: { type: 'success', text: `执行完成：处理${d.processed}条，提炼${d.refined}条，跳过${d.skipped}条。` } }));
                } else {
                  message.error('执行失败，查看日志详情');
                  setTaskNotices(prev => ({ ...prev, [task.id]: { type: 'error', text: '执行失败，请在下方执行日志查看详情。' } }));
                }
                refreshLiveStatus().catch(() => undefined);
                setTimeout(() => { setProgress(null); loadTasks(); loadLogs(1); loadRefineCats(); refreshLiveStatus().catch(() => undefined); }, 3000);
              }
            }
          } catch (e: any) {
            console.log('[poll] 出错:', e.message);
            setTaskNotices(prev => ({ ...prev, [task.id]: { type: 'error', text: `实时进度查询失败：${e.message || '网络异常'}` } }));
            clearInterval(poll);
          }
        }, 2000);
      } else {
        console.log('[trigger] 失败:', r.data?.error);
        message.error(r.data?.error || '触发失败');
        setTaskNotices(prev => ({ ...prev, [task.id]: { type: 'error', text: r.data?.error || '触发失败' } }));
        setTriggering(null);
      }
    } catch (e: any) {
      console.log('[trigger] 异常:', e.message, e.response?.status, e.response?.data);
      const err = e?.response?.data?.error || e?.response?.data?.message || e?.message || '触发失败';
      message.error(err);
      setTaskNotices(prev => ({ ...prev, [task.id]: { type: 'error', text: `触发失败：${err}` } }));
      setTriggering(null);
    }
  };

  const pagedDocs = catDocs.slice((docPage - 1) * PAGE_SIZE, docPage * PAGE_SIZE);

  const getSourceTag = (docName: string) => {
    if (docName.startsWith('情报_')) return SOURCE_TAG['情报_'];
    if (docName.startsWith('提炼_')) return SOURCE_TAG['提炼_'];
    return null;
  };

  const isOtherCat = selectedCat ? refineCats.find(c => c.id === selectedCat)?.name === '其他' : false;

  const openDocDetail = async (doc: KbDoc) => {
    setDocDetail(doc);
    setDocDetailText('');
    setDocDetailLoading(true);

    // 调试：检查全局主题对 .ant-modal-title 的影响
    setTimeout(() => {
      const titleEl = document.querySelector('.doc-detail-modal .ant-modal-title');
      const headerEl = document.querySelector('.doc-detail-modal .ant-modal-header');
      const bodyEl = document.querySelector('.doc-detail-modal .ant-modal-body');
      console.log('[DocDetail] titleEl:', titleEl ? window.getComputedStyle(titleEl).color : 'null');
      console.log('[DocDetail] titleEl tag+class:', titleEl ? titleEl.tagName + '.' + titleEl.className : 'null');
      console.log('[DocDetail] headerEl bg:', headerEl ? window.getComputedStyle(headerEl).backgroundColor : 'null');
      console.log('[DocDetail] headerEl color:', headerEl ? window.getComputedStyle(headerEl).color : 'null');
      console.log('[DocDetail] bodyEl color:', bodyEl ? window.getComputedStyle(bodyEl).color : 'null');
      // 找所有 color=white 的来源
      if (titleEl) {
        const matches = document.querySelectorAll('style');
        matches.forEach((s, i) => {
          if (s.textContent?.includes('.ant-modal-title') && s.textContent?.includes('color')) {
            console.log('[DocDetail] style #' + i + ' matches:', s.textContent.slice(0, 200));
          }
        });
      }
    }, 300);

    try {
      const res: any = await kbFetch(`/documents/${encodeURIComponent(doc.name)}/chunks`);
      const chunks = res?.data?.chunks || [];
      setDocDetailText(chunks.map((c: any) => c.text).join('\n\n'));
    } catch (e: any) {
      setDocDetailText('加载失败：' + (e?.message || '未知错误'));
    } finally {
      setDocDetailLoading(false);
    }
  };

  return (
    <div className="ai-refine-panel" style={{ maxWidth: 1200 }}>
      {/* ── 实时日志开关 ── */}
      <Card size="small" style={{ marginBottom: 16, border: liveCollecting ? '1px solid #1677ff' : '1px solid #e8e8e8' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button
            type={liveCollecting ? 'default' : 'primary'}
            icon={liveCollecting ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={liveCollecting ? stopLiveMonitor : startLiveMonitor}
          >
            {liveCollecting ? '关闭提炼任务实时日志' : '开始查看提炼任务实时日志'}
          </Button>
          {!liveCollecting && liveOpen && <Button size="small" onClick={() => setLiveOpen(v => !v)}>{liveOpen ? '收起面板' : '展开面板'}</Button>}
          {liveCollecting && <Tag color="processing" icon={<SyncOutlined spin />}>正在采集，每3秒刷新</Tag>}
          {lastLiveRefresh && <Text type="secondary" style={{ fontSize: 12 }}>最近刷新：{lastLiveRefresh}</Text>}
          <Text type="secondary" style={{ fontSize: 12 }}>点击“手动执行一次”后也会自动打开这里，展示本地提炼、经验提炼的状态、步骤、处理/提炼/跳过数量。</Text>
        </div>
      </Card>

      {/* ── 提炼任务实时监控面板 ── */}
      {liveOpen && (
        <Card size="small" style={{ marginBottom: 16, background: '#fbfdff' }}
          title={<Space size={6}>{liveCollecting ? <SyncOutlined spin /> : <EyeOutlined />}<Text strong style={{ fontSize: 13 }}>提炼任务实时日志 / 状态 / 处理过程</Text><Text type="secondary" style={{ fontSize: 11 }}>{liveCollecting ? '实时采集中' : '已暂停采集'}</Text></Space>}
          extra={<Button size="small" onClick={() => refreshLiveStatus().catch(() => message.error('刷新实时日志失败'))}>立即刷新</Button>}
        >
          {Object.keys(liveStatus).length === 0 ? (
            <Alert type="info" showIcon message="还没有采集到实时状态" description="点击上方“开始查看提炼任务实时日志”，系统会每3秒采集一次文档提炼、经验提炼的运行日志。" />
          ) : (
            <Row gutter={[12, 12]}>
              {liveTaskTypes.map(type => {
                const s = liveStatus[type];
                if (!s) return null;
                const color = TASK_COLORS[type];
                const running = !!s.isRunning;
                const failed = s.status === 'failed';
                const success = s.status === 'success' && !running;
                return (
                  <Col xs={24} md={8} key={type}>
                    <div style={{ height: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${running ? color : failed ? '#ffccc7' : '#e8e8e8'}`, background: '#fff' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        <Tag color={running ? 'processing' : failed ? 'error' : success ? 'success' : 'default'} style={{ margin: 0 }}>
                          {running ? '运行中' : failed ? '失败' : success ? '已完成' : '空闲'}
                        </Tag>
                        <Text strong>{TASK_LABELS[type]}</Text>
                        {running && <Spin size="small" style={{ marginLeft: 'auto' }} />}
                      </div>
                      <Progress percent={Math.min(Number(s.progress || 0), 100)} size="small" status={failed ? 'exception' : success ? 'success' : running ? 'active' : 'normal'} strokeColor={color} />
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginTop: 8, marginBottom: 8 }}>
                        <div style={{ background: '#f7f7f7', borderRadius: 6, padding: 6, textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 11 }}>处理</Text><div style={{ fontWeight: 700 }}>{s.processed || 0}</div></div>
                        <div style={{ background: '#f6ffed', borderRadius: 6, padding: 6, textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 11 }}>提炼</Text><div style={{ fontWeight: 700, color: '#52c41a' }}>{s.refined || 0}</div></div>
                        <div style={{ background: '#fff7e6', borderRadius: 6, padding: 6, textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 11 }}>跳过</Text><div style={{ fontWeight: 700, color: '#fa8c16' }}>{s.skipped || 0}</div></div>
                      </div>
                      <Text style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>当前：{s.currentStep || '等待任务'}</Text>
                      {s.startedAt && <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>开始：{dayjs(s.startedAt).format('MM-DD HH:mm:ss')}{s.finishedAt ? ` ｜结束：${dayjs(s.finishedAt).format('MM-DD HH:mm:ss')}` : ''}</Text>}
                      {s.errorMessage && <Text type="danger" style={{ display: 'block', fontSize: 11, marginTop: 4 }}>{s.errorMessage}</Text>}
                      <div style={{ marginTop: 8, maxHeight: 150, overflow: 'auto', borderTop: '1px solid #f0f0f0', paddingTop: 6 }}>
                        {(s.steps || []).length === 0 ? <Text type="secondary" style={{ fontSize: 12 }}>暂无步骤日志</Text> : (s.steps || []).map((step: any, i: number) => (
                          <div key={`${type}-${i}`} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, marginBottom: 4 }}>
                            {step.progress >= 100 ? <CheckCircleOutlined style={{ color: failed ? '#ff4d4f' : '#52c41a', marginTop: 2 }} /> : <SyncOutlined style={{ color, marginTop: 2 }} />}
                            <div style={{ minWidth: 0 }}>
                              <Text style={{ fontSize: 12 }}>{step.step}</Text>
                              <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>{typeof step.progress === 'number' ? `${step.progress}%` : ''}</Text>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card>
      )}

      {/* ── 任务卡片 ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 12, marginBottom: 16 }}>
        {configurableTasks.map(task => {
          const color = TASK_COLORS[task.taskType];
          const icon = <ThunderboltOutlined />;
          return (
            <Card key={task.id} size="small"
              style={{ borderTop: `3px solid ${task.enabled ? color : '#d9d9d9'}` }}
              title={
                <Space size={8}>
                  <span style={{ color: task.enabled ? color : '#999', fontSize: 15 }}>{icon}</span>
                  <Text strong style={{ fontSize: 14 }}>{TASK_LABELS[task.taskType]}</Text>
                  {task.isRunning && <Tag color="processing" style={{ marginLeft: 4 }}>运行中</Tag>}
                  {task.enabled && !task.isRunning && <Tag color="default" style={{ marginLeft: 4 }}>待调度</Tag>}
                </Space>
              }
              extra={<Switch size="small" checked={task.enabled} onChange={() => toggleTask(task)} />}
            >
              {/* ── 第1行：执行模式 · 每轮条数 ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>频率</Text>
                <Select size="small" value={task.frequencyMinutes} style={{ width: 110 }} onChange={v => updateFreq(task, v)} options={FREQ_OPTIONS} />
                <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>每轮</Text>
                <InputNumber size="small" min={1} max={50} value={task.maxPerRun} style={{ width: 60 }} onChange={v => updateMaxPerRun(task, v)} />
                <Text type="secondary" style={{ fontSize: 12 }}>条</Text>
                {task.lastRunAt && <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}><ClockCircleOutlined /> {dayjs(task.lastRunAt).format('MM-DD HH:mm')}</Text>}
              </div>

              {/* ── 第2行：模型 ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>模型</Text>
                <Select size="small" style={{ flex: 1, minWidth: 180, maxWidth: 320 }}
                  value={task.modelId || undefined} placeholder="默认(DeepSeek)" allowClear
                  onChange={v => updateModel(task, v)}
                  options={models.map(m => ({ label: `${m.name || m.code}${m.provider ? ` (${m.provider.name})` : ''}`, value: m.id }))}
                />
              </div>

              {/* ── 手动触发 ── */}
              <Button size="small" type="primary" ghost icon={<PlayCircleOutlined />}
                loading={triggering === task.id}
                onClick={() => triggerOnce(task)}
                style={{ marginTop: 4 }}
              >手动执行一次</Button>
              {taskNotices[task.id] && (
                <Alert
                  type={taskNotices[task.id].type}
                  showIcon
                  style={{ marginTop: 8, padding: '4px 8px', fontSize: 12 }}
                  message={taskNotices[task.id].text}
                />
              )}
            </Card>
          );
        })}
      </div>

      {/* ── 统计 ── */}
      {stats && Object.keys(stats.byType).length > 0 && (
        <Card size="small" title={<><BarChartOutlined style={{ marginRight: 6 }} />提炼统计</>} style={{ marginBottom: 16 }}
          extra={<Select size="small" value={statsPeriod} onChange={setStatsPeriod} style={{ width: 90 }} options={[{ label: '今天', value: 'daily' },{ label: '本周', value: 'weekly' },{ label: '全部', value: 'all' }]} />}>
          <Row gutter={12}>
            {Object.entries(stats.byType).map(([type, data]) => (
              <Col xs={12} sm={8} md={6} key={type}>
                <div style={{ background: '#fafafa', borderRadius: 6, padding: '10px 14px' }}>
                  <Text strong style={{ fontSize: 13 }}>{TASK_LABELS[type] || type}</Text>
                  <Row gutter={4} style={{ marginTop: 6 }}>
                    <Col span={8}><Statistic title="处理" value={data.processed} valueStyle={{ fontSize: 16 }} /></Col>
                    <Col span={8}><Statistic title="提炼" value={data.refined} valueStyle={{ fontSize: 16, color: '#52c41a' }} /></Col>
                    <Col span={8}><Statistic title="跳过" value={data.skipped} valueStyle={{ fontSize: 16, color: '#faad14' }} /></Col>
                  </Row>
                  <Text type="secondary" style={{ fontSize: 11 }}>共 {data.runs} 次</Text>
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* ── 知识库数据 ── */}
      <Card size="small" title={<><DatabaseOutlined style={{ marginRight: 6 }} />知识库数据</>} style={{ marginBottom: 16 }} loading={catLoading}>
        {refineCats.length === 0 && !catLoading && <Text type="secondary">暂无数据，开启提炼任务后自动产生</Text>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginBottom: selectedCat ? 16 : 0 }}>
          {refineCats.map(cat => {
            const active = selectedCat === cat.id;
            return (
              <div key={cat.id}
                onClick={() => { if (active) { setSelectedCat(null); setCatDocs([]); } else { setSelectedCat(cat.id); loadCatDocs(cat.id); } }}
                style={{
                  position: 'relative',
                  cursor: 'pointer', borderRadius: 6, padding: '10px 12px',
                  border: `1px solid ${active ? cat.color || '#1677ff' : '#e8e8e8'}`,
                  background: active ? '#f6ffed' : '#fff',
                  transition: 'all .15s',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                  <Text style={{ fontSize: 11, color: '#888', minWidth: 0 }} ellipsis={{ tooltip: cat.name }}>{cat.name.replace('提炼·', '').replace('AI情报·', '')}</Text>
                  <Popconfirm
                    overlayClassName="kb-pop-light"
                    title="确认清除该类型数据？"
                    description={`只删除「${cat.name}」分类下的 ${cat.docCount} 条数据，不删除分类，也不影响原有知识库。`}
                    okText="确认清除"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                    disabled={cat.docCount <= 0 || clearingCatId === cat.id}
                    onConfirm={(e) => { e?.stopPropagation?.(); clearCategoryDocs(cat); }}
                    onCancel={(e) => e?.stopPropagation?.()}
                  >
                    <Button
                      size="small"
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      loading={clearingCatId === cat.id}
                      disabled={cat.docCount <= 0}
                      onClick={(e) => e.stopPropagation()}
                      title="一键清除该类型数据"
                      style={{ width: 24, height: 22, minWidth: 24, padding: 0 }}
                    />
                  </Popconfirm>
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: cat.name === '其他' ? '#faad14' : cat.color || '#52c41a', marginTop: 2 }}>
                  {cat.docCount} <Text style={{ fontSize: 12, fontWeight: 400, color: '#999' }}>条</Text>
                </div>
                <Text type="secondary" style={{ display: 'block', fontSize: 11, marginTop: 4 }}>
                  最新：{formatDocTime(cat.latestCreatedAt)}
                </Text>
              </div>
            );
          })}
        </div>

        {selectedCat && (
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <Space size={8}>
                <FolderOpenOutlined style={{ color: '#1677ff' }} />
                <Text strong style={{ fontSize: 14 }}>{refineCats.find(c => c.id === selectedCat)?.name}</Text>
                <Tag>{catDocsAll.length} 条</Tag>
                {isOtherCat && <Tag color="orange">AI建议分类见详情</Tag>}
              </Space>
              <Input placeholder="搜索..." prefix={docSearchLoading ? <Spin size="small" /> : <SearchOutlined />} size="small" style={{ width: 200 }} allowClear
                value={docSearch} onChange={e => handleDocSearch(e.target.value)} />
            </div>
            <Spin spinning={docLoading}>
              {catDocs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, color: '#bbb' }}>
                  <FileTextOutlined style={{ fontSize: 32 }} />
                  <p style={{ marginTop: 8 }}>{docSearch ? '无匹配结果' : '暂无数据'}</p>
                </div>
              ) : (
                <>
                  <List size="small" dataSource={pagedDocs} style={{ maxHeight: 360, overflow: 'auto' }}
                    renderItem={(doc: KbDoc) => {
                      const srcTag = getSourceTag(doc.name);
                      let suggestedCat = '';
                      if (doc.name.startsWith('情报_') && isOtherCat) {
                        const inner = doc.name.replace('情报_', '');
                        const lastUnderscore = inner.lastIndexOf('_');
                        if (lastUnderscore > 0) suggestedCat = inner.slice(0, lastUnderscore);
                      }
                      return (
                        <List.Item style={{ padding: '5px 10px', cursor: 'pointer' }}
                          onClick={() => openDocDetail(doc)}>
                          <Space size={4}>
                            {srcTag && <Tag color={srcTag.color} style={{ fontSize: 10, lineHeight: '16px' }}>{srcTag.text}</Tag>}
                            <Text style={{ fontSize: 12 }}>{TYPE_ICONS[doc.type] || '📎'}</Text>
                            <Text style={{ fontSize: 13, maxWidth: 420, color: '#1677ff' }} ellipsis={{ tooltip: doc.name }}>{doc.name}</Text>
                            {suggestedCat && <Tooltip title="AI建议的新分类"><Tag color="orange" style={{ fontSize: 10 }}>{suggestedCat}</Tag></Tooltip>}
                          </Space>
                          <Space size={6} wrap>
                            <Tag style={{ fontSize: 10 }}>{doc.type}</Tag>
                            <Text type="secondary" style={{ fontSize: 11 }}>{doc.chunkCount}块</Text>
                            <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                              创建：{formatDocTime(doc.createdAt)}
                            </Text>
                          </Space>
                        </List.Item>
                      );
                    }}
                  />
                  <div style={{ textAlign: 'center', marginTop: 8 }}>
                    <Space size={4}>
                      <Button size="small" disabled={docPage <= 1} onClick={() => setDocPage(p => p - 1)}>‹ 上一页</Button>
                      <Text type="secondary" style={{ fontSize: 12 }}>{docPage}/{Math.ceil(catDocs.length / PAGE_SIZE) || 1}（{catDocs.length}条）</Text>
                      <Button size="small" disabled={docPage >= Math.ceil(catDocs.length / PAGE_SIZE)} onClick={() => setDocPage(p => p + 1)}>下一页 ›</Button>
                    </Space>
                  </div>
                </>
              )}
            </Spin>
          </div>
        )}
      </Card>

      {/* ── 执行日志 ── */}
      <Card size="small" title={<><HistoryOutlined style={{ marginRight: 6 }} />执行日志</>}
        extra={
          <Space size={6}>
            <Select size="small" value={taskFilter} onChange={setTaskFilter} style={{ width: 90 }}
              options={[{ label: '全部', value: 'all' },{ label: '本地', value: 'doc' },{ label: '经验', value: 'exp' },{ label: '网络', value: 'web' }]} />
            <Button size="small" onClick={() => loadLogs(logPage)} loading={logLoading}>刷新</Button>
          </Space>
        }>
        <Table<RefineLog> size="small" loading={logLoading} dataSource={logs} rowKey="id"
          pagination={{ current: logPage, total: logTotal, pageSize: 15, onChange: (p: number) => setLogPage(p), showTotal: (t: number) => `共 ${t} 条` }}
          columns={[
            { title: '类型', dataIndex: 'taskType', width: 70, render: (v: string) => <Tag color={TASK_COLORS[v]} style={{ fontSize: 11 }}>{TASK_LABELS[v]}</Tag> },
            { title: '状态', dataIndex: 'status', width: 70,
              render: (v: string) => {
                const m: Record<string,{color:string;text:string}> = { running:{color:'processing',text:'执行中'}, success:{color:'success',text:'完成'}, failed:{color:'error',text:'失败'} };
                return <Tag color={m[v]?.color} style={{ fontSize: 11 }}>{m[v]?.text||v}</Tag>;
              },
            },
            { title: '处理', dataIndex: 'itemsProcessed', width: 50 },
            { title: '提炼', dataIndex: 'itemsRefined', width: 50, render: (v: number) => <Text style={{ color: '#52c41a' }}>{v}</Text> },
            { title: '跳过', dataIndex: 'itemsSkipped', width: 50 },
            { title: '开始', dataIndex: 'startedAt', width: 130, render: (v: string) => dayjs(v).format('MM-DD HH:mm:ss') },
            { title: '耗时', width: 70, render: (_:any,r:RefineLog) => {
              if(!r.finishedAt) return <Spin size="small"/>;
              const ms=dayjs(r.finishedAt).diff(dayjs(r.startedAt));
              if(ms<1000) return `${ms}ms`; if(ms<60000) return `${(ms/1000).toFixed(0)}s`; return `${(ms/60000).toFixed(1)}m`;
            }},
            { title: '', width: 50, render: (_:any,r:RefineLog) => <Button type="link" size="small" onClick={()=>setLogDetail(r)}>详情</Button> },
          ]}
          locale={{ emptyText: '暂无执行记录' }}
        />
      </Card>

      {/* ── 日志详情弹窗 ── */}
      <Modal
        title={<span style={{ color: '#111', fontWeight: 800 }}>执行详情</span>}
        open={!!logDetail}
        onCancel={() => setLogDetail(null)}
        footer={null}
        width={680}
        styles={{
          header: { background: '#fff', borderBottom: '1px solid #e8e8e8' },
          body: { background: '#fff', padding: '16px 24px' },
          content: { background: '#fff' },
        }}
      >
        {logDetail && (
          <Descriptions column={1} size="small" bordered
            styles={{
              label: { background: '#f5f5f5', color: '#333', fontWeight: 700, fontSize: 13 },
              content: { background: '#fff', color: '#222', fontSize: 13 },
            }}
            labelStyle={{ color: '#333', fontWeight: 700 }}
            contentStyle={{ color: '#222' }}
          >
            <Descriptions.Item label={<span style={{ color: '#333' }}>类型</span>}>
              <Tag color={TASK_COLORS[logDetail.taskType]}>{TASK_LABELS[logDetail.taskType]}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={<span style={{ color: '#333' }}>状态</span>}>
              <Tag color={logDetail.status==='success'?'success':logDetail.status==='failed'?'error':'processing'}>{logDetail.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={<span style={{ color: '#333' }}>处理/提炼/跳过</span>}>
              <span style={{ color: '#222' }}>{logDetail.itemsProcessed} / {logDetail.itemsRefined} / {logDetail.itemsSkipped}</span>
            </Descriptions.Item>
            <Descriptions.Item label={<span style={{ color: '#333' }}>开始</span>}>
              <span style={{ color: '#222' }}>{dayjs(logDetail.startedAt).format('YYYY-MM-DD HH:mm:ss')}</span>
            </Descriptions.Item>
            <Descriptions.Item label={<span style={{ color: '#333' }}>结束</span>}>
              <span style={{ color: '#222' }}>{logDetail.finishedAt ? dayjs(logDetail.finishedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}</span>
            </Descriptions.Item>
            {logDetail.errorMessage && (
              <Descriptions.Item label={<span style={{ color: '#333' }}>错误</span>}>
                <span style={{ color: '#d00' }}>{logDetail.errorMessage}</span>
              </Descriptions.Item>
            )}
            {logDetail.details && logDetail.details.length > 0 && (
              <Descriptions.Item label={<span style={{ color: '#333' }}>明细</span>}>
                <div style={{ maxHeight: 300, overflow: 'auto', background: '#fff' }}>
                  {logDetail.details.map((d: any, i: number) => (
                    <div key={i} style={{ marginBottom: 6, padding: '8px 10px', background: '#f6f8fa', borderRadius: 4, border: '1px solid #e1e4e8' }}>
                      {d.step && (
                        <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: '#111', fontWeight: 700, fontSize: 13 }}>{d.step}</span>
                          {typeof d.progress === 'number' && <span style={{ color: '#666', fontSize: 11 }}>{d.progress}%</span>}
                        </div>
                      )}
                      {d.sourceUrl && <div style={{ marginBottom: 2 }}><span style={{ color: '#555', fontSize: 11 }}>{String(d.sourceUrl).slice(0, 100)}</span></div>}
                      {d.sourceName && <div style={{ marginBottom: 2 }}><span style={{ color: '#111', fontWeight: 700, fontSize: 12 }}>{String(d.sourceName).slice(0, 80)}</span></div>}
                      {d.title && !d.sourceUrl && <div style={{ marginBottom: 2 }}><span style={{ color: '#111', fontWeight: 700, fontSize: 13 }}>{String(d.title).slice(0, 80)}</span></div>}
                      {d.category && <Tag color="green" style={{ marginRight: 4 }}>{d.category}</Tag>}
                      {d.newCategory && <Tag color="orange" style={{ marginRight: 4 }}>AI建议: {d.newCategory}</Tag>}
                      {d.summary && <div style={{ marginTop: 4 }}><span style={{ color: '#444', fontSize: 12, lineHeight: '18px' }}>{String(d.summary)}</span></div>}
                      {d.error && <div style={{ marginTop: 4 }}><span style={{ color: '#c00', fontWeight: 700, fontSize: 12 }}>{String(d.error)}</span></div>}
                    </div>
                  ))}
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* ── 文档详情弹窗 ── */}
      <Modal
        rootClassName="doc-detail-modal"
        title={<span style={{ color: '#111', fontWeight: 800 }}>AI 提炼内容</span>}
        open={!!docDetail}
        onCancel={() => { setDocDetail(null); setDocDetailText(''); }}
        footer={null}
        width={760}
        styles={{
          header: { background: '#fff', borderBottom: '1px solid #e8e8e8' },
          body: { background: '#fff', padding: '16px 24px', maxHeight: '70vh', overflow: 'auto' },
          content: { background: '#fff' },
        }}
      >
        {docDetail && (
          <>
            <style>{`
              .doc-detail-modal .ant-modal-header { background: #fff !important; border-bottom-color: #e8e8e8 !important; }
              .doc-detail-modal .ant-modal-header .ant-modal-title { color: #111 !important; }
              .doc-detail-modal .ant-modal-body { color: #111 !important; background: #fff !important; }
              .doc-detail-modal .ant-modal-body .ant-typography { color: #111 !important; }
              .doc-detail-modal .ant-modal-body .ant-tag { color: #333 !important; }
              .doc-detail-modal .ant-modal-content { background: #fff !important; }
              .doc-detail-modal .ant-modal-close { color: #555 !important; }
            `}</style>
            <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px dashed #d9d9d9' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>{docDetail.name}</span>
              <div style={{ marginTop: 6 }}>
                <Space size={6}>
                  <Tag style={{ color: '#333' }}>{docDetail.type}</Tag>
                  <span style={{ fontSize: 12, color: '#555' }}>{docDetail.chunkCount} 块</span>
                  <span style={{ fontSize: 12, color: '#555' }}>{docDetail.createdAt ? dayjs(docDetail.createdAt).format('YYYY-MM-DD HH:mm') : ''}</span>
                </Space>
              </div>
            </div>
            <Spin spinning={docDetailLoading}>
              <div style={{
                whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.8, minHeight: 80,
                background: '#fafafa', color: '#111', padding: 16, borderRadius: 6,
                border: '1px solid #e8e8e8'
              }}>
                {docDetailText || (docDetailLoading ? '' : '（无内容）')}
              </div>
            </Spin>
          </>
        )}
      </Modal>

      {/* ── 分类清除进度弹窗 ── */}
      <Modal
        title={<span style={{ color: '#111', fontWeight: 800 }}>正在清除分类数据</span>}
        open={!!clearProgress?.open}
        onCancel={() => {
          if (clearProgress?.status !== 'running') setClearProgress(null);
        }}
        footer={clearProgress?.status === 'running' ? null : <Button type="primary" onClick={() => setClearProgress(null)}>知道了</Button>}
        closable={clearProgress?.status !== 'running'}
        maskClosable={false}
        width={560}
        styles={{
          header: { background: '#fff', borderBottom: '1px solid #e8e8e8' },
          body: { background: '#fff', padding: '18px 24px' },
          content: { background: '#fff' },
          footer: { background: '#fff', borderTop: '1px solid #e8e8e8' },
        }}
      >
        {clearProgress && (
          <div style={{ color: '#111' }}>
            <Alert
              type={clearProgress.status === 'error' ? 'error' : clearProgress.status === 'warning' ? 'warning' : clearProgress.status === 'success' ? 'success' : 'info'}
              showIcon
              message={`清除「${clearProgress.catName}」`}
              description={clearProgress.total > 0
                ? `已处理 ${clearProgress.done}/${clearProgress.total} 条，成功 ${clearProgress.ok} 条，失败 ${clearProgress.failed} 条。`
                : clearProgress.current}
              style={{ marginBottom: 14 }}
            />
            <Progress
              percent={clearProgress.total > 0 ? Math.round((clearProgress.done / clearProgress.total) * 100) : (clearProgress.status === 'running' ? 15 : 100)}
              status={clearProgress.status === 'error' ? 'exception' : clearProgress.status === 'success' ? 'success' : 'active'}
            />
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
              <Text style={{ color: '#334155', fontSize: 13 }}>当前处理：</Text>
              <div style={{ marginTop: 4, color: '#0f172a', fontSize: 13, wordBreak: 'break-all' }}>{clearProgress.current}</div>
            </div>
            {clearProgress.status === 'running' && (
              <Text type="secondary" style={{ display: 'block', marginTop: 10, fontSize: 12 }}>
                正在逐条删除知识库向量和索引，请不要重复点击删除。
              </Text>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
